import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { auth, firestore } from '../config/firebase';
// Modular Firestore API for web
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot as onSnapshotModular,
  query as queryModular,
  orderBy as orderByModular,
  runTransaction as runTransactionModular,
  getDocs
} from 'firebase/firestore';
import { Alert, Platform } from 'react-native';
import { IncomeFrequency, Income } from '../types/income';
import { useSubscription } from './SubscriptionContext';

console.log('GoalsContext: Starting context initialization');

export type GoalType = 'Savings' | 'Debt' | 'Investment';
export type PriorityType = 'Essential' | 'Important' | 'Optional';

export interface Payment {
  id: string;
  amount: number;
  date: string;
  note?: string;
}

export interface Goal {
  id: string;
  name: string;
  type: GoalType;
  targetAmount: number;
  currentAmount: number;
  deadline: string;
  monthlyContribution: number;
  priority: PriorityType;
  allocatedPercentage: number;
  payments: Payment[];
  completedDate?: string | null;
  createdAt: string;
  lastUpdated: string;
  apr?: number; // Annual Percentage Rate for Savings goals
  originalTargetAmount?: number;
  loanTermYears?: number;
  loanTermMonths?: number;
  startDate?: string; // Start date for debt goals
}

// Moved from GoalsScreen.tsx - must be outside any component
export const calculateEstimatedCompletion = (goal: Partial<Goal>) => {
  if (
    !goal.targetAmount ||
    !goal.monthlyContribution ||
    goal.currentAmount === undefined
  ) {
    return null;
  }

  // Simplified calculation for Savings/Investment
  const remainingAmount = goal.targetAmount - goal.currentAmount;
  if (remainingAmount <= 0) {
    // If goal is met or exceeded, return today's date
    return new Date().toISOString().split('T')[0];
  }
  
  if (goal.monthlyContribution <= 0) {
    return null; // Avoid division by zero or infinite loops
  }

  const monthsToComplete = Math.ceil(remainingAmount / goal.monthlyContribution);

  if (monthsToComplete >= 600) { // Cap at 50 years
    return null;
  }

  const estimatedDate = new Date();
  estimatedDate.setMonth(estimatedDate.getMonth() + monthsToComplete);
  return estimatedDate.toISOString().split('T')[0];
};

interface GoalsContextType {
  goals: Goal[];
  income: Income;
  addGoal: (goal: Omit<Goal, 'id'>) => Promise<void>;
  updateGoal: (goal: Goal) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  updateIncome: (income: Income) => void;
  addPayment: (goalId: string, payment: Omit<Payment, 'id'>) => Promise<void>;
  deletePayment: (goalId: string, paymentId: string) => Promise<void>;
}

const GoalsContext = createContext<GoalsContextType | undefined>(undefined);

export const useGoals = () => {
  const context = useContext(GoalsContext);
  if (!context) {
    console.error('GoalsContext: useGoals must be used within a GoalsProvider');
    throw new Error('useGoals must be used within a GoalsProvider');
  }
  return context;
};

const initialGoals: Goal[] = [
  {
    id: '1',
    name: 'Emergency Fund',
    type: 'Savings',
    targetAmount: 10000,
    currentAmount: 3500,
    deadline: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year from now
    monthlyContribution: 500,
    priority: 'Essential',
    allocatedPercentage: 30,
    payments: [],
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Car Loan',
    type: 'Debt',
    targetAmount: 25000,
    currentAmount: 15000,
    deadline: new Date(Date.now() + 547 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1.5 years from now
    monthlyContribution: 800,
    priority: 'Important',
    allocatedPercentage: 40,
    payments: [],
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Stock Portfolio',
    type: 'Investment',
    targetAmount: 50000,
    currentAmount: 12000,
    deadline: new Date(Date.now() + 730 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 2 years from now
    monthlyContribution: 1000,
    priority: 'Optional',
    allocatedPercentage: 20,
    payments: [],
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString()
  }
];

const initialIncome: Income = {
  amount: 0, // Default monthly amount
  frequency: 'monthly',
  monthlyIncome: 0, // Same as amount for monthly frequency
  unallocatedAmount: 0 // Initially no unallocated amount
};

interface GoalsProviderProps {
  children: ReactNode;
}

const calculateMonthlyIncome = (amount: number, frequency: IncomeFrequency): number => {
  switch (frequency) {
    case 'bi-weekly':
      return Math.round((amount * 26) / 12); // 26 pay periods per year
    case 'weekly':
      return Math.round((amount * 52) / 12); // 52 weeks per year
    case 'daily':
      return Math.round(amount * 30); // Approximate 30 days per month
    case 'monthly':
    default:
      return amount;
  }
};

interface UpdateIncomeParams {
  amount?: number;
  frequency?: IncomeFrequency;
}

export const GoalsProvider: React.FC<GoalsProviderProps> = ({ children }) => {
  // Helper: true if using modular web Firestore
  const isModularFirestore = Platform.OS === 'web';
  console.log('GoalsProvider: Initializing provider');
  
  const [goals, setGoals] = useState<Goal[]>([]);
  const [income, setIncome] = useState<Income>(initialIncome);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Get subscription context - handle case where it might not be available
  let subscriptionContext;
  try {
    subscriptionContext = useSubscription();
  } catch (error) {
    console.warn('Subscription context not available yet:', error);
    subscriptionContext = null;
  }

  // Subscribe to auth state and goals collection
  useEffect(() => {
    // Guard: Only set up listeners if Firebase is initialized
    // For web, auth and firestore are objects, for native they are functions
    const isAuthReady = typeof auth === 'function' ? !!auth() : !!auth;
    const isFirestoreReady = typeof firestore === 'function' ? !!firestore() : !!firestore;
    if (!isAuthReady || !isFirestoreReady) {
      setAuthError('Firebase initialization error');
      return;
    }

    let unsubscribeIncome: (() => void) | undefined;
    let unsubscribeGoals: (() => void) | undefined;

    // Helper to clean up listeners and state
    const cleanup = () => {
      if (unsubscribeIncome) unsubscribeIncome();
      if (unsubscribeGoals) unsubscribeGoals();
      setGoals([]);
      setIncome(initialIncome);
      setIsInitialized(false);
      setAuthError(null);
    };

    // Get the correct auth instance for both web (object) and native (function)
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const firestoreInstance = typeof firestore === 'function' ? firestore() : firestore;

    // Listen to auth state changes
    const unsubscribeAuth = authInstance.onAuthStateChanged(async (user: any) => {
      // Always clean up listeners on user change
      if (unsubscribeIncome) { unsubscribeIncome(); unsubscribeIncome = undefined; }
      if (unsubscribeGoals) { unsubscribeGoals(); unsubscribeGoals = undefined; }

      // Clear any previous auth errors when switching users
      setAuthError(null);

      if (!user) {
        setGoals([]);
        setIncome(initialIncome);
        setIsInitialized(true);
        setAuthError(null);
        return;
      }

      try {
        let userRef, userDoc, userGoalsRef;
        if (isModularFirestore) {
          userRef = doc(firestoreInstance, 'users', user.uid);
          userDoc = await getDoc(userRef);
          if (!userDoc.exists()) {
            await setDoc(userRef, { income: initialIncome, createdAt: new Date().toISOString() });
          }
          // Subscribe to income
          unsubscribeIncome = onSnapshotModular(userRef, (docSnap) => {
            const docData = docSnap.data();
            if (docSnap.exists() && docData && docData.income) {
              setIncome(docData.income);
            } else {
              setIncome(initialIncome);
            }
          }, (error: any) => {
            // Handle authentication errors gracefully
            if (error.code === 'permission-denied' || error.message.includes('permission')) {
              console.log('GoalsContext: Permission denied for income sync (likely during logout), ignoring error');
              // Don't set auth error for permission issues during logout
              return;
            }
            console.error('GoalsContext: Income sync error:', error);
            setAuthError('Income sync error: ' + error.message);
          });
          // Subscribe to goals
          userGoalsRef = collection(firestoreInstance, 'users', user.uid, 'goals');
          const goalsQuery = queryModular(userGoalsRef, orderByModular('createdAt', 'desc'));
          unsubscribeGoals = onSnapshotModular(goalsQuery, (snapshot) => {
            const updatedGoals: Goal[] = [];
            snapshot.forEach((docSnap) => {
              const data = docSnap.data();
              updatedGoals.push({
                id: docSnap.id,
                name: data.name,
                type: data.type,
                targetAmount: data.targetAmount,
                currentAmount: data.currentAmount,
                deadline: data.deadline,
                monthlyContribution: data.monthlyContribution,
                priority: data.priority,
                allocatedPercentage: data.allocatedPercentage,
                payments: data.payments || [],
                completedDate: data.completedDate,
                createdAt: data.createdAt || new Date().toISOString(),
                lastUpdated: data.lastUpdated || new Date().toISOString(),
                ...(data.type === 'Debt' && {
                  apr: data.apr,
                  originalTargetAmount: data.originalTargetAmount,
                  loanTermYears: data.loanTermYears,
                  loanTermMonths: data.loanTermMonths
                })
              } as Goal);
            });
            setGoals(updatedGoals);
            setIsInitialized(true);
          }, (error: any) => {
            // Handle authentication errors gracefully
            if (error.code === 'permission-denied' || error.message.includes('permission')) {
              console.log('GoalsContext: Permission denied for goals sync (likely during logout), ignoring error');
              // Don't set auth error for permission issues during logout
              setIsInitialized(true);
              return;
            }
            console.error('GoalsContext: Goals sync error:', error);
            setAuthError('Goals sync error: ' + error.message);
            setIsInitialized(true);
          });
        } else {
          userRef = firestoreInstance.collection('users').doc(user.uid);
          userDoc = await userRef.get();
          if (!userDoc.exists) {
            await userRef.set({ income: initialIncome, createdAt: new Date().toISOString() });
          }
          unsubscribeIncome = userRef.onSnapshot(
            (doc: any) => {
              const docData = doc.data();
              if (doc.exists && docData && docData.income) {
                setIncome(docData.income);
              } else {
                setIncome(initialIncome);
              }
            },
            (error: any) => {
              setAuthError('Income sync error: ' + error.message);
            }
          );
          userGoalsRef = firestoreInstance.collection('users').doc(user.uid).collection('goals');
          unsubscribeGoals = userGoalsRef
            .orderBy('createdAt', 'desc')
            .onSnapshot(
              (snapshot: any) => {
                const updatedGoals: Goal[] = [];
                snapshot.forEach((doc: any) => {
                  const data = doc.data();
                  updatedGoals.push({
                    id: doc.id,
                    name: data.name,
                    type: data.type,
                    targetAmount: data.targetAmount,
                    currentAmount: data.currentAmount,
                    deadline: data.deadline,
                    monthlyContribution: data.monthlyContribution,
                    priority: data.priority,
                    allocatedPercentage: data.allocatedPercentage,
                    payments: data.payments || [],
                    completedDate: data.completedDate,
                    createdAt: data.createdAt || new Date().toISOString(),
                    lastUpdated: data.lastUpdated || new Date().toISOString(),
                    ...(data.type === 'Debt' && {
                      apr: data.apr,
                      originalTargetAmount: data.originalTargetAmount,
                      loanTermYears: data.loanTermYears,
                      loanTermMonths: data.loanTermMonths
                    })
                  } as Goal);
                });
                setGoals(updatedGoals);
                setIsInitialized(true);
              },
              (error: any) => {
                setAuthError('Goals sync error: ' + error.message);
                setIsInitialized(true);
              }
            );
        }
      } catch (error) {
        setAuthError('Setup error: ' + (error instanceof Error ? error.message : 'Unknown error'));
        setIsInitialized(true);
      }
    });

    // Cleanup on unmount
    return () => {
      cleanup();
      unsubscribeAuth();
    };
  }, []);

  const calculateDeadline = useCallback((currentAmount: number, targetAmount: number, monthlyContribution: number) => {
    if (!monthlyContribution || monthlyContribution <= 0) return '';
    const remainingAmount = targetAmount - currentAmount;
    const monthsNeeded = Math.ceil(remainingAmount / monthlyContribution);
    const deadlineDate = new Date();
    deadlineDate.setMonth(deadlineDate.getMonth() + monthsNeeded);
    return deadlineDate.toISOString().split('T')[0];
  }, []);

  const addGoal = useCallback(async (goalData: Omit<Goal, 'id'>) => {
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const firestoreInstance = typeof firestore === 'function' ? firestore() : firestore;
    try {
      if (!authInstance.currentUser) throw new Error('You must be logged in to add goals');

      // Subscription check
      if (subscriptionContext) {
        const { canCreateGoal, getMaxGoals } = subscriptionContext;
        if (!canCreateGoal(goals.length)) {
          const maxGoals = getMaxGoals();
          Alert.alert(
            'Goal Limit Reached',
            `You currently have ${goals.length} goals, but your subscription only allows ${maxGoals} goals.`,
            [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Upgrade', onPress: () => {} }
            ]
          );
          throw new Error(`Goal limit reached. Maximum ${maxGoals} goals allowed.`);
        }
      }

      const user = authInstance.currentUser;
      if (!user) throw new Error('No authenticated user');

      if (isModularFirestore) {
        // Modular API (web)
        const userGoalsRef = collection(firestoreInstance, 'users', user.uid, 'goals');
        const newGoalRef = doc(userGoalsRef);
        const newGoal: Goal = {
          ...goalData,
          id: newGoalRef.id,
          currentAmount: goalData.currentAmount || 0,
          monthlyContribution: (income.monthlyIncome * goalData.allocatedPercentage) / 100,
          payments: [],
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        };
        await setDoc(newGoalRef, newGoal);
        // Update income's unallocated amount
        const userRef = doc(firestoreInstance, 'users', user.uid);
        const totalAllocated = goals.reduce((sum, goal) => sum + goal.allocatedPercentage, 0) + goalData.allocatedPercentage;
        const newUnallocatedAmount = (income.monthlyIncome * (100 - totalAllocated)) / 100;
        await updateDoc(userRef, { 'income.unallocatedAmount': newUnallocatedAmount });
      } else {
        // Classic API (native)
        const userGoalsRef = firestoreInstance.collection('users').doc(user.uid).collection('goals');
        const newGoalRef = userGoalsRef.doc();
        const newGoal: Goal = {
          ...goalData,
          id: newGoalRef.id,
          currentAmount: goalData.currentAmount || 0,
          monthlyContribution: (income.monthlyIncome * goalData.allocatedPercentage) / 100,
          payments: [],
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        };
        await newGoalRef.set(newGoal);
        const userRef = firestoreInstance.collection('users').doc(user.uid);
        const totalAllocated = goals.reduce((sum, goal) => sum + goal.allocatedPercentage, 0) + goalData.allocatedPercentage;
        const newUnallocatedAmount = (income.monthlyIncome * (100 - totalAllocated)) / 100;
        await userRef.update({ 'income.unallocatedAmount': newUnallocatedAmount });
      }
    } catch (error) {
      console.error('Error adding goal:', error);
      Alert.alert('Error', error instanceof Error ? `Failed to add goal: ${error.message}` : 'Failed to add goal. Please try again.');
      throw error;
    }
  }, [income.monthlyIncome, goals, subscriptionContext]);

  const updateGoal = useCallback(async (updatedGoal: Goal) => {
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const firestoreInstance = typeof firestore === 'function' ? firestore() : firestore;
    try {
      if (!authInstance.currentUser) throw new Error('You must be logged in to update goals');
      const user = authInstance.currentUser;
      if (!user) throw new Error('No authenticated user');
      const userId = user.uid;
      // Calculate the new monthly contribution based on allocation
      const monthlyContribution = Math.round((income.monthlyIncome * updatedGoal.allocatedPercentage) / 100);
      // Create goal data without undefined values
      const goalData: any = {
        name: updatedGoal.name,
        type: updatedGoal.type,
        targetAmount: updatedGoal.targetAmount,
        currentAmount: updatedGoal.currentAmount,
        deadline: updatedGoal.deadline,
        monthlyContribution,
        priority: updatedGoal.priority,
        allocatedPercentage: updatedGoal.allocatedPercentage,
        payments: updatedGoal.payments || [],
        completedDate: updatedGoal.currentAmount >= updatedGoal.targetAmount 
          ? (updatedGoal.completedDate || new Date().toISOString())
          : null,
        lastUpdated: new Date().toISOString()
      };
      if (updatedGoal.type === 'Debt') {
        if (typeof (updatedGoal as any).apr === 'number') goalData.apr = (updatedGoal as any).apr;
        if (typeof (updatedGoal as any).originalTargetAmount === 'number') goalData.originalTargetAmount = (updatedGoal as any).originalTargetAmount;
        if (typeof (updatedGoal as any).loanTermYears === 'number') goalData.loanTermYears = (updatedGoal as any).loanTermYears;
        if (typeof (updatedGoal as any).loanTermMonths === 'number') goalData.loanTermMonths = (updatedGoal as any).loanTermMonths;
      }
      const totalAllocated = goals.reduce((sum, goal) => goal.id === updatedGoal.id ? sum + updatedGoal.allocatedPercentage : sum + goal.allocatedPercentage, 0);
      if (isModularFirestore) {
        // Modular API (web)
        const goalRef = doc(firestoreInstance, 'users', userId, 'goals', updatedGoal.id);
        const userRef = doc(firestoreInstance, 'users', userId);
        // Use runTransaction from modular API
        await runTransactionModular(firestoreInstance, async (transaction) => {
          const goalDoc = await transaction.get(goalRef);
          if (!goalDoc.exists()) throw new Error('Goal document not found');
          const userDoc = await transaction.get(userRef);
          if (!userDoc.exists()) throw new Error('User document not found');
          const userData = userDoc.data();
          const currentIncome = userData ? userData.income : undefined;
          // Read all other goals
          const otherGoalsQuery = queryModular(collection(firestoreInstance, 'users', userId, 'goals'));
          const otherGoalsSnap = await getDocs(otherGoalsQuery);
          const otherGoalsDocs = otherGoalsSnap.docs.filter(docSnap => docSnap.id !== updatedGoal.id);
          const newUnallocatedAmount = Math.round((income.monthlyIncome * (100 - totalAllocated)) / 100);
          const updatedIncome = { ...currentIncome, unallocatedAmount: newUnallocatedAmount };
          // Update the goal
          transaction.update(goalRef, goalData);
          // Update income
          transaction.update(userRef, { income: updatedIncome, lastUpdated: new Date().toISOString() });
          // Update other goals' monthly contributions
          otherGoalsDocs.forEach((docSnap) => {
            const currentGoal = goals.find(g => g.id === docSnap.id);
            if (currentGoal) {
              const newMonthlyContribution = Math.round((income.monthlyIncome * currentGoal.allocatedPercentage) / 100);
              transaction.update(doc(firestoreInstance, 'users', userId, 'goals', docSnap.id), {
                monthlyContribution: newMonthlyContribution,
                lastUpdated: new Date().toISOString()
              });
            }
          });
        });
      } else {
        // Classic API (native)
        const goalRef = firestoreInstance.collection('users').doc(userId).collection('goals').doc(updatedGoal.id);
        const userRef = firestoreInstance.collection('users').doc(userId);
        await firestoreInstance.runTransaction(async (transaction: any) => {
          const goalDoc = await transaction.get(goalRef);
          if (!goalDoc.exists) throw new Error('Goal document not found');
          const userDoc = await transaction.get(userRef);
          if (!userDoc.exists) throw new Error('User document not found');
          const userData = userDoc.data();
          const currentIncome = userData ? userData.income : undefined;
          const otherGoalsRefs = goals.filter(g => g.id !== updatedGoal.id).map(g => firestoreInstance.collection('users').doc(userId).collection('goals').doc(g.id));
          const otherGoalsDocs = await Promise.all(otherGoalsRefs.map(ref => transaction.get(ref)));
          const newUnallocatedAmount = Math.round((income.monthlyIncome * (100 - totalAllocated)) / 100);
          const updatedIncome = { ...currentIncome, unallocatedAmount: newUnallocatedAmount };
          transaction.update(goalRef, goalData);
          transaction.update(userRef, { income: updatedIncome, lastUpdated: new Date().toISOString() });
          otherGoalsDocs.forEach((docSnap, index) => {
            if (docSnap.exists) {
              const goalRef = otherGoalsRefs[index];
              const currentGoal = goals.filter(g => g.id !== updatedGoal.id)[index];
              const newMonthlyContribution = Math.round((income.monthlyIncome * currentGoal.allocatedPercentage) / 100);
              transaction.update(goalRef, { monthlyContribution: newMonthlyContribution, lastUpdated: new Date().toISOString() });
            }
          });
        });
      }
      // Update local states
      setGoals(prevGoals => prevGoals.map(g => {
        if (g.id === updatedGoal.id) {
          return { ...g, ...goalData, id: g.id, createdAt: g.createdAt };
        }
        const newMonthlyContribution = Math.round((income.monthlyIncome * g.allocatedPercentage) / 100);
        return { ...g, monthlyContribution: newMonthlyContribution, lastUpdated: new Date().toISOString() };
      }));
      setIncome(prev => ({ ...prev, unallocatedAmount: Math.round((income.monthlyIncome * (100 - totalAllocated)) / 100) }));
    } catch (error) {
      console.error('Error updating goal:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update goal');
    }
  }, [income.monthlyIncome, goals, subscriptionContext]);

  const deleteGoal = useCallback(async (id: string) => {
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const firestoreInstance = typeof firestore === 'function' ? firestore() : firestore;
    try {
      if (!authInstance.currentUser) throw new Error('No authenticated user');
      const goalToDelete = goals.find(g => g.id === id);
      if (!goalToDelete) throw new Error('Goal not found');
      const user = authInstance.currentUser;
      if (!user) throw new Error('No authenticated user');
      const userId = user.uid;
      const remainingAllocation = goals.filter(g => g.id !== id).reduce((sum, goal) => sum + goal.allocatedPercentage, 0);
      const newUnallocatedAmount = Math.round((income.monthlyIncome * (100 - remainingAllocation)) / 100);
      if (isModularFirestore) {
        // Modular API (web)
        const goalRef = doc(firestoreInstance, 'users', userId, 'goals', id);
        const userRef = doc(firestoreInstance, 'users', userId);
        await runTransactionModular(firestoreInstance, async (transaction) => {
          transaction.delete(goalRef);
          transaction.update(userRef, { 'income.unallocatedAmount': newUnallocatedAmount, lastUpdated: new Date().toISOString() });
        });
      } else {
        // Classic API (native)
        const goalRef = firestoreInstance.collection('users').doc(userId).collection('goals').doc(id);
        const userRef = firestoreInstance.collection('users').doc(userId);
        await goalRef.delete();
        await userRef.update({ 'income.unallocatedAmount': newUnallocatedAmount, lastUpdated: new Date().toISOString() });
      }
      setGoals(prevGoals => prevGoals.filter(g => g.id !== id));
      setIncome(prev => ({ ...prev, unallocatedAmount: newUnallocatedAmount }));
    } catch (error) {
      console.error('Error deleting goal:', error);
      Alert.alert('Error', 'Failed to delete goal. Please try again.');
      throw error;
    }
  }, [goals, income.monthlyIncome]);

  const updateIncome = useCallback(async (newIncome: Income) => {
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const firestoreInstance = typeof firestore === 'function' ? firestore() : firestore;
    const user = authInstance.currentUser;
    if (!user) {
      console.warn('updateIncome called but no authenticated user. Skipping update.');
      return;
    }
    try {
      const userId = user.uid;
      const monthlyIncome = calculateMonthlyIncome(newIncome.amount, newIncome.frequency);
      const totalAllocated = goals.reduce((sum, goal) => sum + goal.allocatedPercentage, 0);
      const unallocatedAmount = Math.round((monthlyIncome * (100 - totalAllocated)) / 100);
      const updatedIncome = { ...newIncome, monthlyIncome, unallocatedAmount };
      if (isModularFirestore) {
        // Modular API (web)
        const userRef = doc(firestoreInstance, 'users', userId);
        await runTransactionModular(firestoreInstance, async (transaction) => {
          const userDoc = await transaction.get(userRef);
          if (!userDoc.exists()) throw new Error('User document not found');
          // Get current goals
          const goalsQuery = queryModular(collection(firestoreInstance, 'users', userId, 'goals'));
          const goalsSnap = await getDocs(goalsQuery);
          const currentGoals = goalsSnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as Goal));
          const freshTotalAllocated = currentGoals.reduce((sum, goal) => sum + goal.allocatedPercentage, 0);
          const freshUnallocatedAmount = Math.round((monthlyIncome * (100 - freshTotalAllocated)) / 100);
          const finalIncome = { ...updatedIncome, unallocatedAmount: freshUnallocatedAmount };
          transaction.update(userRef, { income: finalIncome, lastUpdated: new Date().toISOString() });
          for (const goal of currentGoals) {
            const goalRef = doc(firestoreInstance, 'users', userId, 'goals', goal.id);
            const newMonthlyContribution = Math.round((monthlyIncome * goal.allocatedPercentage) / 100);
            const newDeadline = calculateDeadline(goal.currentAmount, goal.targetAmount, newMonthlyContribution);
            transaction.update(goalRef, { monthlyContribution: newMonthlyContribution, deadline: newDeadline, lastUpdated: new Date().toISOString() });
          }
        });
      } else {
        // Classic API (native)
        await firestoreInstance.runTransaction(async (transaction: any) => {
          const userRef = firestoreInstance.collection('users').doc(userId);
          const userDoc = await transaction.get(userRef);
          if (!userDoc.exists) throw new Error('User document not found');
          const goalsSnapshot = await userRef.collection('goals').get();
          const currentGoals = goalsSnapshot.docs.map((docSnap: any) => ({ id: docSnap.id, ...docSnap.data() } as Goal));
          const freshTotalAllocated = currentGoals.reduce((sum: any, goal: any) => sum + goal.allocatedPercentage, 0);
          const freshUnallocatedAmount = Math.round((monthlyIncome * (100 - freshTotalAllocated)) / 100);
          const finalIncome = { ...updatedIncome, unallocatedAmount: freshUnallocatedAmount };
          transaction.update(userRef, { income: finalIncome, lastUpdated: new Date().toISOString() });
          for (const goal of currentGoals) {
            const goalRef = userRef.collection('goals').doc(goal.id);
            const newMonthlyContribution = Math.round((monthlyIncome * goal.allocatedPercentage) / 100);
            const newDeadline = calculateDeadline(goal.currentAmount, goal.targetAmount, newMonthlyContribution);
            transaction.update(goalRef, { monthlyContribution: newMonthlyContribution, deadline: newDeadline, lastUpdated: new Date().toISOString() });
          }
        });
      }
    } catch (error) {
      console.error('Error updating income:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update income');
    }
  }, [goals, calculateMonthlyIncome, calculateDeadline]);

  const addPayment = useCallback(async (goalId: string, payment: Omit<Payment, 'id'>) => {
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const firestoreInstance = typeof firestore === 'function' ? firestore() : firestore;
    const user = authInstance.currentUser;
    if (!user) return;
    if (isModularFirestore) {
      // Modular API (web)
      const goalRef = doc(firestoreInstance, 'users', user.uid, 'goals', goalId);
      await runTransactionModular(firestoreInstance, async (transaction) => {
        const goalDoc = await transaction.get(goalRef);
        if (!goalDoc.exists()) throw new Error('Goal does not exist!');
        const goalData = goalDoc.data() as Goal;
        const newPayment = { ...payment, id: Date.now().toString() };
        const newPayments = [...goalData.payments, newPayment];
        const newCurrentAmount = goalData.currentAmount + payment.amount;
        const updateData: any = {
          payments: newPayments,
          currentAmount: newCurrentAmount,
          lastUpdated: new Date().toISOString(),
        };
        if (goalData.type === 'Savings' || goalData.type === 'Investment') {
          const newDeadline = calculateEstimatedCompletion({ ...goalData, currentAmount: newCurrentAmount });
          if (newDeadline) updateData.deadline = newDeadline;
          if (newCurrentAmount >= goalData.targetAmount) updateData.completedDate = new Date().toISOString();
        } else if (goalData.type === 'Debt' && goalData.originalTargetAmount) {
          if (newCurrentAmount >= goalData.originalTargetAmount) {
            updateData.targetAmount = goalData.originalTargetAmount;
            updateData.completedDate = new Date().toISOString();
            updateData.monthlyContribution = 0;
          }
          if (goalData.loanTermYears === undefined && goalData.apr) {
            const calculateCreditCardPayoff = (originalAmount: number, currentAmount: number, apr: number, monthlyPayment: number) => {
              if (!originalAmount || !apr || isNaN(originalAmount) || isNaN(currentAmount) || isNaN(apr) || originalAmount <= currentAmount) {
                return new Date().toISOString().split('T')[0];
              }
              const remainingBalance = originalAmount - currentAmount;
              const dailyRate = apr / 100 / 365;
              let balance = remainingBalance;
              let totalInterest = 0;
              let months = 0;
              const maxMonths = 600;
              while (balance > 0.01 && months < maxMonths) {
                let monthInterest = 0;
                for (let d = 0; d < 30; d++) {
                  const dailyInterest = balance * dailyRate;
                  balance += dailyInterest;
                  monthInterest += dailyInterest;
                }
                totalInterest += monthInterest;
                const payment = Math.min(monthlyPayment, balance);
                balance -= payment;
                months++;
              }
              const payoff = new Date();
              payoff.setMonth(payoff.getMonth() + months);
              return payoff.toISOString().split('T')[0];
            };
            const newDeadline = calculateCreditCardPayoff(goalData.originalTargetAmount, newCurrentAmount, goalData.apr, goalData.monthlyContribution);
            if (newDeadline) updateData.deadline = newDeadline;
          }
        }
        transaction.update(goalRef, updateData);
      });
    } else {
      // Classic API (native)
      const goalRef = firestoreInstance.collection('users').doc(user.uid).collection('goals').doc(goalId);
      await firestoreInstance.runTransaction(async (transaction: any) => {
        const goalDoc = await transaction.get(goalRef);
        if (!goalDoc.exists) throw new Error('Goal does not exist!');
        const goalData = goalDoc.data() as Goal;
        const newPayment = { ...payment, id: Date.now().toString() };
        const newPayments = [...goalData.payments, newPayment];
        const newCurrentAmount = goalData.currentAmount + payment.amount;
        const updateData: any = {
          payments: newPayments,
          currentAmount: newCurrentAmount,
          lastUpdated: new Date().toISOString(),
        };
        if (goalData.type === 'Savings' || goalData.type === 'Investment') {
          const newDeadline = calculateEstimatedCompletion({ ...goalData, currentAmount: newCurrentAmount });
          if (newDeadline) updateData.deadline = newDeadline;
          if (newCurrentAmount >= goalData.targetAmount) updateData.completedDate = new Date().toISOString();
        } else if (goalData.type === 'Debt' && goalData.originalTargetAmount) {
          if (newCurrentAmount >= goalData.originalTargetAmount) {
            updateData.targetAmount = goalData.originalTargetAmount;
            updateData.completedDate = new Date().toISOString();
            updateData.monthlyContribution = 0;
          }
          if (goalData.loanTermYears === undefined && goalData.apr) {
            const calculateCreditCardPayoff = (originalAmount: number, currentAmount: number, apr: number, monthlyPayment: number) => {
              if (!originalAmount || !apr || isNaN(originalAmount) || isNaN(currentAmount) || isNaN(apr) || originalAmount <= currentAmount) {
                return new Date().toISOString().split('T')[0];
              }
              const remainingBalance = originalAmount - currentAmount;
              const dailyRate = apr / 100 / 365;
              let balance = remainingBalance;
              let totalInterest = 0;
              let months = 0;
              const maxMonths = 600;
              while (balance > 0.01 && months < maxMonths) {
                let monthInterest = 0;
                for (let d = 0; d < 30; d++) {
                  const dailyInterest = balance * dailyRate;
                  balance += dailyInterest;
                  monthInterest += dailyInterest;
                }
                totalInterest += monthInterest;
                const payment = Math.min(monthlyPayment, balance);
                balance -= payment;
                months++;
              }
              const payoff = new Date();
              payoff.setMonth(payoff.getMonth() + months);
              return payoff.toISOString().split('T')[0];
            };
            const newDeadline = calculateCreditCardPayoff(goalData.originalTargetAmount, newCurrentAmount, goalData.apr, goalData.monthlyContribution);
            if (newDeadline) updateData.deadline = newDeadline;
          }
        }
        transaction.update(goalRef, updateData);
      });
    }
  }, []);

  const deletePayment = useCallback(async (goalId: string, paymentId: string) => {
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const firestoreInstance = typeof firestore === 'function' ? firestore() : firestore;
    const user = authInstance.currentUser;
    if (!user) return;
    if (isModularFirestore) {
      // Modular API (web)
      const goalRef = doc(firestoreInstance, 'users', user.uid, 'goals', goalId);
      await runTransactionModular(firestoreInstance, async (transaction) => {
        const goalDoc = await transaction.get(goalRef);
        if (!goalDoc.exists()) throw new Error('Goal does not exist!');
        const goalData = goalDoc.data() as Goal;
        const paymentToDelete = goalData.payments.find((p: any) => p.id === paymentId);
        if (!paymentToDelete) return;
        const newPayments = goalData.payments.filter((p: any) => p.id !== paymentId);
        const newCurrentAmount = goalData.currentAmount - paymentToDelete.amount;
        const updateData: any = {
          payments: newPayments,
          currentAmount: newCurrentAmount,
          lastUpdated: new Date().toISOString(),
        };
        if (goalData.type === 'Savings' || goalData.type === 'Investment') {
          const newDeadline = calculateEstimatedCompletion({ ...goalData, currentAmount: newCurrentAmount });
          if (newDeadline) updateData.deadline = newDeadline;
        } else if (goalData.type === 'Debt' && goalData.originalTargetAmount) {
          if (goalData.loanTermYears === undefined && goalData.apr) {
            const calculateCreditCardPayoff = (originalAmount: number, currentAmount: number, apr: number, monthlyPayment: number) => {
              if (!originalAmount || !apr || isNaN(originalAmount) || isNaN(currentAmount) || isNaN(apr) || originalAmount <= currentAmount) {
                return new Date().toISOString().split('T')[0];
              }
              const remainingBalance = originalAmount - currentAmount;
              const dailyRate = apr / 100 / 365;
              let balance = remainingBalance;
              let totalInterest = 0;
              let months = 0;
              const maxMonths = 600;
              while (balance > 0.01 && months < maxMonths) {
                let monthInterest = 0;
                for (let d = 0; d < 30; d++) {
                  const dailyInterest = balance * dailyRate;
                  balance += dailyInterest;
                  monthInterest += dailyInterest;
                }
                totalInterest += monthInterest;
                const payment = Math.min(monthlyPayment, balance);
                balance -= payment;
                months++;
              }
              const payoff = new Date();
              payoff.setMonth(payoff.getMonth() + months);
              return payoff.toISOString().split('T')[0];
            };
            const newDeadline = calculateCreditCardPayoff(goalData.originalTargetAmount, newCurrentAmount, goalData.apr, goalData.monthlyContribution);
            if (newDeadline) updateData.deadline = newDeadline;
          }
        }
        transaction.update(goalRef, updateData);
      });
    } else {
      // Classic API (native)
      const goalRef = firestoreInstance.collection('users').doc(user.uid).collection('goals').doc(goalId);
      await firestoreInstance.runTransaction(async (transaction: any) => {
        const goalDoc = await transaction.get(goalRef);
        if (!goalDoc.exists) throw new Error('Goal does not exist!');
        const goalData = goalDoc.data() as Goal;
        const paymentToDelete = goalData.payments.find((p: any) => p.id === paymentId);
        if (!paymentToDelete) return;
        const newPayments = goalData.payments.filter((p: any) => p.id !== paymentId);
        const newCurrentAmount = goalData.currentAmount - paymentToDelete.amount;
        const updateData: any = {
          payments: newPayments,
          currentAmount: newCurrentAmount,
          lastUpdated: new Date().toISOString(),
        };
        if (goalData.type === 'Savings' || goalData.type === 'Investment') {
          const newDeadline = calculateEstimatedCompletion({ ...goalData, currentAmount: newCurrentAmount });
          if (newDeadline) updateData.deadline = newDeadline;
        } else if (goalData.type === 'Debt' && goalData.originalTargetAmount) {
          if (goalData.loanTermYears === undefined && goalData.apr) {
            const calculateCreditCardPayoff = (originalAmount: number, currentAmount: number, apr: number, monthlyPayment: number) => {
              if (!originalAmount || !apr || isNaN(originalAmount) || isNaN(currentAmount) || isNaN(apr) || originalAmount <= currentAmount) {
                return new Date().toISOString().split('T')[0];
              }
              const remainingBalance = originalAmount - currentAmount;
              const dailyRate = apr / 100 / 365;
              let balance = remainingBalance;
              let totalInterest = 0;
              let months = 0;
              const maxMonths = 600;
              while (balance > 0.01 && months < maxMonths) {
                let monthInterest = 0;
                for (let d = 0; d < 30; d++) {
                  const dailyInterest = balance * dailyRate;
                  balance += dailyInterest;
                  monthInterest += dailyInterest;
                }
                totalInterest += monthInterest;
                const payment = Math.min(monthlyPayment, balance);
                balance -= payment;
                months++;
              }
              const payoff = new Date();
              payoff.setMonth(payoff.getMonth() + months);
              return payoff.toISOString().split('T')[0];
            };
            const newDeadline = calculateCreditCardPayoff(goalData.originalTargetAmount, newCurrentAmount, goalData.apr, goalData.monthlyContribution);
            if (newDeadline) updateData.deadline = newDeadline;
          }
        }
        transaction.update(goalRef, updateData);
      });
    }
  }, []);

  // Show error state only for critical auth/connection issues, not temporary sync issues
  if (authError && authError.includes('Firebase initialization error')) {
    console.error('Critical Auth/Connection Error:', authError);
    Alert.alert('Connection Error', 'There was a problem connecting to the service. Please check your internet connection and try again.');
    return null;
  }
  
  // Log sync errors but don't show connection dialog for them
  if (authError && (authError.includes('Income sync error') || authError.includes('Goals sync error'))) {
    console.warn('Sync error (non-critical):', authError);
    // Don't show connection error dialog for sync issues
  }

  if (!isInitialized) {
    console.log('Provider not yet initialized');
    return null;
  }

  const value = {
    goals,
    income,
    addGoal,
    updateGoal,
    deleteGoal,
    updateIncome,
    addPayment,
    deletePayment
  };

  return (
    <GoalsContext.Provider value={value}>
      {children}
    </GoalsContext.Provider>
  );
}; 